import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useErrorService, ABSpinner } from '@abroad/components'
import API from '../utils/API'
import { getFormattedDateRange, getProgramDatesRange } from '../utils/date'
import { openLinkInTab } from '../utils/openLinkInTab'

import Imgix from 'react-imgix'
import 'lazysizes'
import 'lazysizes/plugins/parent-fit/ls.parent-fit'
import 'lazysizes/plugins/attrchange/ls.attrchange'

const isLocalAssetUrl = (url) =>
  typeof url === 'string' &&
  (url.startsWith('/assets/') || (url.startsWith('/') && !url.startsWith('//')))

const MOBILE_BREAKPOINT = 768
const HERO_DESKTOP_WIDTH = 3200
const HERO_MOBILE_WIDTH = 1600

// Event cards are ~half-width × 400–500px CSS and scale up to ~1.15× (parallax).
const EVENT_CARD_WIDTH = 1200
const EVENT_CARD_HEIGHT = 750

// Low-quality blurred placeholder shown immediately while the real Imgix image loads.
const IMGIX_LQIP_PARAMS = 'fill=blur&blur=300&px=16&auto=format&q=10'

// react-imgix fixed-size mode otherwise emits dpr=2&q=50 (etc.), which softens
// retina images. Keep quality stable across DPR; avoid auto=compress.
const IMGIX_SHARP_PARAMS = {
  auto: 'format',
  q: 100,
  cs: 'srgb',
  lossless: 1,
}

const getHeroImageDimensions = (isMobile) => {
  const width = isMobile ? HERO_MOBILE_WIDTH : HERO_DESKTOP_WIDTH
  // Mobile hero ar=9:16, desktop hero ar=21:9
  const height = Math.round((width * (isMobile ? 16 : 9)) / (isMobile ? 9 : 21))
  return { width, height }
}

const CAROUSEL_IMAGE_FILES = [
  'Bhutan_1.jpg',
  'Bhutan_2.jpg',
  'Bhutan_3.jpg',
  'Bhutan_4.jpg',
  'Bhutan_5.jpg',
  'Bhutan_6.jpg',
  'Bhutan_7.jpg',
  'Bhutan_8.jpg',
]

const PAGE_INTRO = {
  title: 'Abroad Event Calendar',
  description:
    'Our experiences invite you into the heart of the world’s great wisdom cultures, guided by teachers and elders who carry knowledge that has shaped human flourishing for centuries.',
}

const CAROUSEL_IMAGES = CAROUSEL_IMAGE_FILES.map((fileName) => ({
  url: `${process.env.REACT_APP_IMG_SOURCE}/${fileName}`,
  caption: PAGE_INTRO.title,
}))

function EventCardMedia({ imageUrl, title }) {
  const [imageFailed, setImageFailed] = useState(false)
  const trimmedUrl = typeof imageUrl === 'string' ? imageUrl.trim() : ''
  const hasUrl = Boolean(trimmedUrl)

  useEffect(() => {
    setImageFailed(false)
  }, [trimmedUrl])

  const showPlaceholder = !hasUrl || imageFailed

  if (showPlaceholder) {
    return (
      <div
        className='parallax-container parallax-height journey-card-media-placeholder d-flex align-items-center justify-content-center'
        role='img'
        aria-label={title}>
        <p className='text-slate-gray small lh-relaxed font-paragraph small-paragraph'>
          {title}
        </p>
      </div>
    )
  }

  return (
    <div className='parallax-container parallax-height'>
      {isLocalAssetUrl(trimmedUrl) ? (
        <img
          src={trimmedUrl}
          alt={title}
          className='w-100 h-100 object-cover parallax-image'
          onError={() => setImageFailed(true)}
          loading='lazy'
          decoding='async'
          style={{
            objectFit: 'cover',
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        />
      ) : (
        <Imgix
          className='lazyload w-100 h-100 object-cover parallax-image'
          src={trimmedUrl}
          width={EVENT_CARD_WIDTH}
          height={EVENT_CARD_HEIGHT}
          disableQualityByDPR
          imgixParams={{
            fit: 'crop',
            ...IMGIX_SHARP_PARAMS,
          }}
          attributeConfig={{
            src: 'data-src',
            srcSet: 'data-srcset',
            sizes: 'data-sizes',
          }}
          htmlAttributes={{
            src: `${trimmedUrl}?w=${EVENT_CARD_WIDTH}&h=${EVENT_CARD_HEIGHT}&fit=crop&${IMGIX_LQIP_PARAMS}`,
            alt: title,
            onError: () => setImageFailed(true),
            decoding: 'async',
            style: {
              objectFit: 'cover',
              position: 'absolute',
              top: 0,
              left: 0,
            },
          }}
        />
      )}
    </div>
  )
}

function EventCard({ item, onClick }) {
  const { start, end } = getProgramDatesRange(item)
  const dateLabel = getFormattedDateRange(start, end)
  const imageUrl = item.imageFileName
    ? `${process.env.REACT_APP_IMG_SOURCE}/quest/${item.imageFileName}`
    : ''

  return (
    <button
      type='button'
      onClick={onClick}
      className='bg-white mb-64px border-radius-custom overflow-hidden d-flex flex-column flex-md-row hover-lift prosperity-card event-calendar-card w-100 text-start border-0 p-0'>
      <div className='col-md-6 p-4 p-lg-5 d-flex flex-column justify-content-center order-2 order-md-1'>
        {dateLabel && (
          <div className='text-gold-light fw-semibold small font-paragraph small-paragraph-2 mb-3'>
            {dateLabel}
          </div>
        )}
        <h3 className='mb-3 fw-medium text-rich-navy prosperity-card-line-height font-heading sub-title'>
          {item.title}
        </h3>
        {item.description && (
          <div
            className='text-slate-gray mb-0 lh-relaxed pre font-paragraph small-paragraph event-calendar-card-description'
            dangerouslySetInnerHTML={{ __html: item.description }}
          />
        )}
      </div>
      <div className='col-md-6 position-relative order-1 order-md-2 overflow-hidden'>
        <EventCardMedia imageUrl={imageUrl} title={item.title} />
      </div>
    </button>
  )
}

const ExperienceEvents = () => {
  const [currentImage, setCurrentImage] = useState(0)
  const [events, setEvents] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isMobileHero, setIsMobileHero] = useState(
    typeof window !== 'undefined'
      ? window.innerWidth < MOBILE_BREAKPOINT
      : false,
  )
  const Error = useErrorService()

  const displayCarouselImages = CAROUSEL_IMAGES
  const heroAspectRatio = isMobileHero ? '9:16' : '21:9'
  const heroImageDimensions = React.useMemo(
    () => getHeroImageDimensions(isMobileHero),
    [isMobileHero],
  )

  useEffect(() => {
    const fetchEvents = async () => {
      setIsLoading(true)
      try {
        const { data } = await API.quests.upcomingQuests()
        setEvents(Array.isArray(data) ? data : [])
      } catch (e) {
        Error.showError(e)
        setEvents([])
      } finally {
        setIsLoading(false)
      }
    }
    fetchEvents()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const handleResize = () => {
      setIsMobileHero(window.innerWidth < MOBILE_BREAKPOINT)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (!displayCarouselImages.length) return
    const timer = setInterval(() => {
      setCurrentImage((current) => (current + 1) % displayCarouselImages.length)
    }, 6000)
    return () => clearInterval(timer)
  }, [displayCarouselImages])

  useEffect(() => {
    const handleScroll = () => {
      document.querySelectorAll('.parallax-image').forEach((imageEl) => {
        const container = imageEl.closest('.parallax-container')
        if (!container) return
        const rect = container.getBoundingClientRect()
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0

        if (isVisible) {
          const viewportPosition =
            (window.innerHeight - rect.top) / (window.innerHeight + rect.height)
          const scale = 1 + 0.15 * Math.min(Math.max(viewportPosition, 0), 1)
          imageEl.style.transform = `scale(${scale})`
        } else {
          imageEl.style.transform = 'scale(1)'
        }
      })
    }
    window.addEventListener('scroll', handleScroll)
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [events])

  const handleEventClick = (item) => {
    openLinkInTab(`/experience/${item._id}`, '_blank')
  }

  return (
    <>
      <Helmet>
        <title>Abroad Event Calendar | Abroad</title>
        <meta name='title' content='Abroad Event Calendar | Abroad' />
        <meta property='og:title' content='Abroad Event Calendar | Abroad' />
        <meta
          property='twitter:title'
          content='Abroad Event Calendar | Abroad'
        />
        <meta name='description' content={PAGE_INTRO.description} />
        <link
          rel='canonical'
          href={`${process.env.REACT_APP_DOMAIN}/experience/events`}
        />
        <meta
          property='og:url'
          content={`${process.env.REACT_APP_DOMAIN}/experience/events`}
        />
        <meta
          property='twitter:url'
          content={`${process.env.REACT_APP_DOMAIN}/experience/events`}
        />
      </Helmet>

      <div className='bg-background prosperity-page experience-events-page'>
        <section className='hero-section position-relative'>
          <div
            id='eventCalendarHeroCarousel'
            className='prosperity-carousel position-relative overflow-hidden'>
            <div className='carousel-inner position-relative h-100'>
              {displayCarouselImages.map((image, index) => (
                <div
                  key={`${image.url}-${index}`}
                  className={`carousel-slide position-absolute w-100 h-100 ${
                    index === currentImage ? 'active' : ''
                  } parallax-container`}>
                  <Imgix
                    className={`d-block w-100 h-100 hero-carousel-image parallax-image ${
                      index === 0 ? '' : 'lazyload'
                    }`}
                    src={image.url}
                    width={heroImageDimensions.width}
                    height={heroImageDimensions.height}
                    disableSrcSet
                    disableQualityByDPR
                    imgixParams={{
                      fit: 'crop',
                      ar: heroAspectRatio,
                      ...IMGIX_SHARP_PARAMS,
                    }}
                    attributeConfig={
                      index === 0
                        ? undefined
                        : {
                            src: 'data-src',
                            srcSet: 'data-srcset',
                            sizes: 'data-sizes',
                          }
                    }
                    htmlAttributes={{
                      alt: image.caption,
                      decoding: 'async',
                      ...(index === 0 && {
                        fetchpriority: 'high',
                        loading: 'eager',
                      }),
                      ...(index !== 0 && {
                        src: `${image.url}?ar=${heroAspectRatio}&fit=crop&${IMGIX_LQIP_PARAMS}`,
                      }),
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
          <nav className='position-absolute top-0 start-0 end-0 pt-3 z-1'>
            <div className='container px-3 d-flex justify-content-center justify-content-md-start'>
              <a href={'https://www.abroad.io/'} className='navbar-brand'>
                <img
                  src='https://images.squarespace-cdn.com/content/v1/63db562b53fc5e6e59ef802c/98522d73-daa5-49ab-bc3c-0468ca75ee95/Group+1989.png'
                  alt='Abroad Logo'
                  style={{ height: '59px', width: '98px' }}
                />
              </a>
            </div>
          </nav>
        </section>

        <main className='content-fade-in'>
          <div className='container px-3 py-5'>
            <div className='event-calendar-intro text-center mx-auto mb-5'>
              <h1 className='font-heading event-calendar-title text-rich-navy mb-4'>
                {PAGE_INTRO.title}
              </h1>
              <p className='font-paragraph event-calendar-description text-rich-navy mb-0 mx-auto'>
                {PAGE_INTRO.description}
              </p>
            </div>

            {isLoading ? (
              <ABSpinner />
            ) : events.length === 0 ? (
              <div className='text-center py-5'>
                <p className='font-paragraph small-paragraph text-slate-gray mb-0'>
                  No upcoming events at this time.
                </p>
              </div>
            ) : (
              <div className='event-calendar-list'>
                {events.map((item) => (
                  <EventCard
                    key={item._id}
                    item={item}
                    onClick={() => handleEventClick(item)}
                  />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  )
}

export default ExperienceEvents
